import { useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation, useMatches, useNavigate, useSearchParams } from 'react-router';
import { Flex } from '@/uicomponents';
import { ROUTES } from '@/lib/constants';
import { isUuid } from '@/lib/utils';
import { useCampaignSummaryQuery } from '@/lib/hooks';
import { useAudienceStore } from '@/stores/audience-store';
import { useAudienceBuilderUiStore } from '@/stores/audience-builder-ui-store';
import { useAudienceDraft } from '@/app/audience-builder/hooks';
import { ConflictModal } from '@/app/audience-builder/components/conflict-modal';
import { RecoveryBanner } from '@/app/audience-builder/components/recovery-banner';
import { CampaignBreadcrumb } from '../campaign-breadcrumb';
import { OnBehalfBar } from '../on-behalf-bar';
import { Show } from '../show';
import { WizardTabs } from '../wizard-tabs';
import { WIZARD_STEPS } from '../wizard-tabs/steps';
import { WizardDraftProvider } from './wizard-draft-context';
import styles from './wizard-layout.module.css';

const WIZARD_PATHS = WIZARD_STEPS.map((s) => s.path);

const isOnNonTargetWizardStep = (pathname: string): boolean =>
  pathname === '/audience-products' ||
  pathname === '/audience-content' ||
  pathname === '/audience-review';

const isInsideWizard = (pathname: string): boolean =>
  WIZARD_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

interface WizardMatchParams {
  id?: string;
}

const readBuilderId = (matches: ReturnType<typeof useMatches>): string | undefined => {
  for (const m of matches) {
    const isWizardMatch = WIZARD_PATHS.some(
      (p) => m.pathname === p || m.pathname.startsWith(`${p}/`),
    );
    if (!isWizardMatch) continue;
    const params = m.params as WizardMatchParams | undefined;
    if (params?.id) return params.id;
  }
  return undefined;
};

export const WizardLayout = () => {
  const matches = useMatches();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const idFromUrl = readBuilderId(matches);

  // NPJB-358: The ?campaignId query param identifies which campaign this
  // audience session belongs to. When it changes (user switches campaigns),
  // the draft hooks must fully reset — but idFromUrl stays undefined for
  // both "new" sessions, so the boot effect's dep array doesn't change.
  // We pass campaignId as part of the draft identity so the hook can
  // detect the switch and reset internal state (autosave queue, refs, etc.).
  const campaignIdFromUrl = searchParams.get('campaignId') ?? undefined;

  // NPJB-358: Breadcrumb needs campaign name. Use URL param first, fall
  // back to store (URL loses ?campaignId after first autosave replaceState).
  const storeCampaignId = useAudienceStore((s) => s.campaignId);
  // NPJB-837: a blank or bad URL id must not hide a good store id.
  const breadcrumbCampaignId = isUuid(campaignIdFromUrl)
    ? campaignIdFromUrl
    : (storeCampaignId ?? undefined);

  // NPJB-837: the same checked read as the campaign page, since both fill one cache entry.
  const { data: campaignForBreadcrumb } = useCampaignSummaryQuery(breadcrumbCampaignId, {
    staleTime: 5 * 60 * 1000,
  });

  const draft = useAudienceDraft({ idFromUrl, campaignIdFromUrl });

  // Tracks whether this wizard session passed through the Target step. A
  // client-side Skip/Continue keeps WizardLayout mounted, so this ref survives
  // forward navigation; a hard refresh / deep-link onto a later step remounts
  // the layout with the ref reset to false.
  const enteredAtTargetRef = useRef(false);

  // Keep the user off the later steps only on a genuine cold entry (no session
  // context). If the URL carries a draft id, the boot hook will hydrate it.
  useEffect(() => {
    if (!isInsideWizard(location.pathname)) return;
    // On the Target step: mark this as an in-flow session and allow it through.
    if (!isOnNonTargetWizardStep(location.pathname)) {
      enteredAtTargetRef.current = true;
      return;
    }
    if (idFromUrl) return;
    const { draftId } = useAudienceStore.getState();
    const { hasUserEdited } = useAudienceBuilderUiStore.getState();
    // Allow in-flow Skip (no draft created yet — Target left no edits). Only
    // bounce a true cold entry: no id, no edits, and the user never came
    // through Target this session.
    if (!draftId && !hasUserEdited && !enteredAtTargetRef.current) {
      navigate(ROUTES.campaigns, { replace: true });
    }
  }, [location.pathname, navigate, idFromUrl]);

  const contextValue = useMemo(
    () => ({
      autosaveState: draft.autosaveState,
      isBooting: draft.isBooting,
      flushBeforeSubmit: draft.flushBeforeSubmit,
      discardPendingSave: draft.discardPendingSave,
    }),
    [draft.autosaveState, draft.isBooting, draft.flushBeforeSubmit, draft.discardPendingSave],
  );

  return (
    <WizardDraftProvider value={contextValue}>
      <Flex vertical className={styles.layout}>
        <OnBehalfBar tenantName={campaignForBreadcrumb?.tenantName ?? undefined} />
        <WizardTabs />
        <Show when={Boolean(breadcrumbCampaignId && campaignForBreadcrumb?.name)}>
          <Flex className={styles.breadcrumb}>
            <CampaignBreadcrumb
              campaignId={breadcrumbCampaignId ?? ''}
              campaignName={campaignForBreadcrumb?.name ?? ''}
              current='New Audience'
            />
          </Flex>
        </Show>
        <RecoveryBanner
          open={draft.showRecoveryBanner}
          onApply={draft.applyLocalRecovery}
          onDiscard={draft.discardLocalRecovery}
        />
        <Outlet />
        <ConflictModal
          open={draft.autosaveState.kind === 'conflict'}
          onReloadLatest={draft.reloadLatestFromServer}
          onKeepMyChanges={draft.keepMyChanges}
        />
      </Flex>
    </WizardDraftProvider>
  );
};
